import dayjs from "dayjs";
import { Alert, Card, NativeSelect, Stack, Typography } from "@altimateai/lego";
import {
  useDatabricksSavingsOverall,
  useDatabricksSavingsOverallEstimate,
  useSearchParam,
} from "../hooks";
import AccordionTitle from "../../../summary/components/AccordionTitle";
import BreakdownSkeleton from "../../../summary/components/BreakdownSkeleton";
import FailureComponent from "../../../summary/components/FailureComponent";
import classes from "../../../summary/summary.module.scss";
import styles from "../../../summary/components/styles.module.scss";
import DatabricksSavingsTiles from "./DatabricksSavingsTiles";
import DatabricksSavingsChart from "./DatabricksSavingsChart";
import SavingsEmptyState from "./SavingsEmptyState";
import type { SavingsData } from "../types";

interface Props {
  isFuture?: boolean;
  startDate: Date;
  endDate: Date | null;
}

const DEFAULT_SAVINGS_PERIOD = "next1Year";

const DatabricksSavingsSection = ({ isFuture = false, startDate, endDate }: Props) => {
  const [savingsPeriodParam, setSavingsPeriod] = useSearchParam("savingsPeriod");
  const savingsPeriod = savingsPeriodParam ?? DEFAULT_SAVINGS_PERIOD;

  const startDateStr = dayjs(startDate).format("YYYY-MM-DD");
  const endDateStr = endDate ? dayjs(endDate).format("YYYY-MM-DD") : "";

  const pastQuery = useDatabricksSavingsOverall(
    { start_date: startDateStr, end_date: endDateStr },
    !isFuture
  );
  const futureQuery = useDatabricksSavingsOverallEstimate(
    {
      start_date: startDateStr,
      end_date: endDateStr,
      savings_period: savingsPeriod,
    },
    isFuture
  );

  const isLoading = isFuture ? futureQuery.isLoading : pastQuery.isLoading;
  const error = isFuture ? futureQuery.error : pastQuery.error;

  if (isLoading) return <BreakdownSkeleton type="section" />;

  // Tiles show the horizon total (annualized projection, rescaled by the
  // This Year / Next 1 Year dropdown). The pie + time donut show the
  // per-month figure, which is dropdown-independent — mirroring Snowflake's
  // tile (annual) vs SavingsInFuture pie (per-month) split. Realized (past)
  // savings have a single window total, so the chart reuses it.
  let savings: SavingsData | null = null;
  let chartSavings: SavingsData | null = null;
  if (isFuture && futureQuery.data) {
    savings = {
      total: futureQuery.data.total_savings_estimate ?? 0,
      autonomous: futureQuery.data.total_autonomous_savings_estimate ?? 0,
      assisted: futureQuery.data.total_assisted_savings_estimate ?? 0,
      timeSaved:
        futureQuery.data.total_time_saved_estimate ?? futureQuery.data.time_saved ?? "0 days",
    };
    chartSavings = {
      total: futureQuery.data.total_savings ?? 0,
      autonomous: futureQuery.data.total_autonomous_savings ?? 0,
      assisted: futureQuery.data.total_assisted_savings ?? 0,
      timeSaved: futureQuery.data.time_saved ?? "0 days",
    };
  } else if (!isFuture && pastQuery.data) {
    savings = {
      total: pastQuery.data.total_savings ?? 0,
      autonomous: pastQuery.data.total_autonomous_savings ?? 0,
      assisted: pastQuery.data.total_assisted_savings ?? 0,
      timeSaved: pastQuery.data.time_saved ?? "0 days",
    };
    chartSavings = savings;
  }

  return (
    <Card
      className={classes.section_card}
      id={isFuture ? "tour-databricks-future-savings" : "tour-databricks-savings"}
    >
      <AccordionTitle
        title={
          isFuture ? (
            <Stack className="al-w-full">
              <Typography variant="section-heading">Savings</Typography>
              <div className="spacer" />
              <NativeSelect
                id="databricks-savings-period-dropdown"
                onChange={value => setSavingsPeriod(value)}
                className={styles.period_selection}
                value={savingsPeriod}
                options={[
                  { value: "thisYear", label: "This Year" },
                  { value: "next1Year", label: "Next 1 Year" },
                ]}
              />
            </Stack>
          ) : (
            <Typography variant="section-heading">Savings</Typography>
          )
        }
        howToText="How to read the charts"
        howToContent={
          <p>
            {isFuture
              ? "This section shows projected savings from Databricks cost optimization opportunities. Autonomous savings are always zero for Databricks as there are no automated optimization agents. Assisted savings show the potential cost reduction if you implement the identified opportunities such as rightsizing oversized clusters and fixing high-failure jobs."
              : "This section shows potential savings from Databricks cost optimization opportunities. Autonomous savings are always zero for Databricks as there are no automated optimization agents. Assisted savings show potential cost reduction from implementing identified opportunities such as rightsizing oversized clusters and fixing high-failure jobs."}
          </p>
        }
      />
      <Stack>
        {error ? (
          <FailureComponent
            title={
              isFuture
                ? "Failed to load projected savings. Please try again later."
                : "Failed to load savings breakdown. Please try again later."
            }
          />
        ) : (
          <>
            <DatabricksSavingsTiles
              isFuture={isFuture}
              startDateStr={startDateStr}
              endDateStr={endDateStr}
              savingsPeriod={savingsPeriod}
              savings={savings}
            />
            <Stack className={classes.graphs}>
              {!chartSavings || chartSavings.total === 0 ? (
                isFuture ? (
                  <Alert>No projected savings opportunities for this period</Alert>
                ) : (
                  <SavingsEmptyState />
                )
              ) : (
                <DatabricksSavingsChart isFuture={isFuture} savings={chartSavings} />
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default DatabricksSavingsSection;
